import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { clearCredentials, setCredentials } from "../../authSlice";
import toast from "react-hot-toast";

function UserManagement() {
  const { token } = useSelector((state) => state.auth);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    role: "",
    area: "",
    openingBalance: 0,
    debitorcredit: "debit",
  });

  const [errors, setErrors] = useState({});
  const { id } = useParams();
  const [allAreas, setAllAreas] = useState([]);
  const [isEditable, setEditable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [tokenExpired, setTokenExpired] = useState(false);

  // Fetch user data if id is present in URL params
  useEffect(() => {
    const fetchUserById = async () => {
      if (!id) return;
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/auth/getUserById?id=${id}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.status === 401) {
          setTokenExpired(true);
          return;
        }

        if (response.ok) {
          const newAccessToken = response.headers.get("Authorization");
          if (newAccessToken) {
            dispatch(setCredentials({ token: newAccessToken.split(" ")[1] }));
          }
          const userData = await response.json();
          if (userData && userData.status === 200) {
            setEditable(!userData.user.isEditable);
            setFormData({
              name: userData.user.name || "",
              email: userData.user.email || "",
              mobile: userData.user.mobile || "",
              role: userData.user.role || "",
              area: userData.user.area || "",
              openingBalance: userData.user.openingBalance || 0,
              debitorcredit: userData.user.debitorcredit || "debit",
            });
          }
        } else {
          const errorData = await response.json();
          console.error("Failed to fetch user data:", errorData.message);
          if (errorData.status === 400) {
            console.log("Invalid user ID");
            navigate("/dashboard");
          }
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    fetchUserById();
  }, [id, token, navigate, dispatch]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    setErrors({});
  };

  const validateForm = () => {
    let formErrors = {};

    if (!formData.name) formErrors.name = "Name is required";
    // if (!formData.email) formErrors.email = "Email is required";
    if (!formData.role) formErrors.role = "Role is required";
    if (!formData.area && formData.role == 'customer') formErrors.area = "Area is required";

    setErrors(formErrors);
    return Object.keys(formErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    var updateData = {
      name: "",
      email: "",
      mobile: "",
    };

    const randEmail = `${formData.mobile || formData.name.replace(/\s+/g, '')}@noemail.local`;
    formData.email = randEmail;
    if (isEditable) {
      updateData.name = formData.name;
      updateData.email =  formData.email;
      updateData.mobile = formData.mobile;
    } else {
      updateData = formData;
    }

    try {
      const url = id
        ? `${process.env.REACT_APP_API_URL}/auth/editUser?id=${id}`
        : `${process.env.REACT_APP_API_URL}/auth/addUser`;
      const method = id ? "PUT" : "POST";

      const response = await fetch(url, {
        method: method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      if (response.status === 401) {
        dispatch(clearCredentials());
        return;
      }

      if (response.ok) {
        const newAccessToken = response.headers.get("Authorization");
        if (newAccessToken) {
          dispatch(setCredentials({ token: newAccessToken.split(" ")[1] }));
        }
        const data = await response.json();
        if (data.status === 201 || data.status === 200) {
          toast.success(
            id ? "User updated successfully" : "User added successfully",
            {
              style: {
                border: "1px solid #021F7E",
                padding: "16px",
                color: "#0573E3",
              },
              iconTheme: {
                primary: "#021F7E",
                secondary: "#0573E3",
              },
            }
          );
          navigate("/dashboard");
        } else {
          console.log(data.message);
        }
      } else {
        toast.error(response.statusText, {
          style: {
            border: "1px solid #FE0000",
            padding: "16px",
            color: "#FE0000",
          },
          iconTheme: {
            primary: "#FE0000",
            secondary: "#FE000099",
          },
          duration: 6000,
        });
        console.error(
          id ? "Failed to update user" : "Failed to add user",
          response.statusText
        );
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  useEffect(() => {
    const fetchAreas = async () => {
      setLoading(true);
      setApiError(null);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/auth/getAllAreas`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.status === 401) {
          setTokenExpired(true);
          return;
        }

        if (response.ok) {
          const newAccessToken = response.headers.get("Authorization");
          if (newAccessToken) {
            dispatch(setCredentials({ token: newAccessToken.split(" ")[1] }));
          }
          const data = await response.json();
          setAllAreas(data || []);
        } else {
          toast.error("error fetching zimidars", {
            style: {
              border: "1px solid #FE0000",
              padding: "16px",
              color: "#FE0000",
            },
            iconTheme: {
              primary: "#FE0000",
              secondary: "#fff",
            },
          });
          throw new Error("Failed to fetch zimidars.");
        }
      } catch (err) {
        toast.error(err.message, {
          style: {
            border: "1px solid #FE0000",
            padding: "16px",
            color: "#FE0000",
          },
          iconTheme: {
            primary: "#FE0000",
            secondary: "#FE000099",
          },
          duration: 6000,
        });
        setApiError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (tokenExpired) {
      dispatch(clearCredentials());
      return;
    }

    if (!token || token === "null") {
      dispatch(setCredentials({ token: null, username: null }));
    }
    if (token) fetchAreas();
  }, [dispatch, token, tokenExpired]);

  if (apiError) return <div>Error: {apiError}</div>;

  return (
    <>
      <div className="whiteBgDiv UserManagementPadding">
        <div className="mainHeading">
          User Management <span>Add/Edit User </span>
        </div>
        {!loading ? (
          <>
            <div className="formHeading UserManagementpage">
              Enter User Detail
            </div>
            <div className="UserManagementGrid">
              <div className="formInputLabelBox">
                <label>Full Name</label>
                <input
                  name="name"
                  type="text"
                  placeholder="Enter here"
                  value={formData.name}
                  onChange={handleInputChange}
                />
                {errors.name && (
                  <span className="errorClass">{errors.name}</span>
                )}
              </div>

              <div className="formInputLabelBox">
                <label>Email Address</label>
                <input
                  name="email"
                  type="text"
                  placeholder="Enter here"
                  value={formData.email}
                  onChange={handleInputChange}
                />
                {errors.email && (
                  <span className="errorClass">{errors.email}</span>
                )}
              </div>

              <div className="formInputLabelBox">
                <label>Mobile Number</label>
                <input
                  name="mobile"
                  type="text"
                  placeholder="Enter here"
                  value={formData.mobile}
                  onChange={handleInputChange}
                />
              </div>

              <div className="formInputLabelBox">
                <label>Role</label>
                <select
                  name="role"
                  className="form-select"
                  disabled={isEditable}
                  value={formData.role}
                  onChange={handleInputChange}
                >
                  <option value="">Select role</option>
                  <option value="zimindar">zimindar</option>
                  <option value="customer">customer</option>
                </select>
                {errors.role && (
                  <span className="errorClass">{errors.role}</span>
                )}
              </div>

              <div className="formInputLabelBox">
                <label>Area</label>
                <select
                  name="area"
                  className="form-select"
                  placeholder="placeholder"
                  disabled={isEditable}
                  value={formData.area}
                  onChange={handleInputChange}
                >
                  <option value="">select Area</option>
                  {allAreas &&
                    allAreas.map((area) => (
                      <option key={area._id} value={area.name}>
                        {area.name}
                      </option>
                    ))}
                </select>
                {errors.area && (
                  <span className="errorClass">{errors.area}</span>
                )}
              </div>

              <div className="formInputLabelBox">
                <label>Opening Balance</label>
                <input
                  name="openingBalance"
                  type="number"
                  placeholder="Enter here"
                  disabled={isEditable}
                  value={formData.openingBalance}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="formInputLabelBox">
              <label>Dr. /Cr.</label>
              <select
                name="debitorcredit"
                className="form-select"
                placeholder="placeholder"
                disabled={isEditable}
                value={formData.type}
                onChange={handleInputChange}
              >
                <option value="debit">Dr.</option>
                <option value="credit">Cr.</option>
              </select>
              {/* <span className="errorClass"></span> */}
            </div>

            <div className="UserManagementBtnFlexDiv">
              {!loading ? (
                <button className="colorBtn" onClick={handleSubmit}>
                  Submit
                </button>
              ) : (
                <button className="colorBtn">Submiting ...</button>
              )}
              <button
                className="grayBtn"
                onClick={() => navigate("/dashboard")}
              >
                back
              </button>
            </div>
          </>
        ) : (
          <div className="formInputLabelBox">
            <label>Loading ...</label>
          </div>
        )}
      </div>
    </>
  );
}

export default UserManagement;
